import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export type TurnstileHandle = {
  reset: () => void;
};

type TurnstileProps = {
  onVerify: (token: string) => void;
  onExpire?: () => void;
};

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(({ onVerify, onExpire }, ref) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval>;

    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: onVerify,
        'expired-callback': () => onExpire?.(),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      pollInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(pollInterval);
          render();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
